import { ChangeEvent, FC, FormEvent } from "react";
import styles from "./styles.module.css";
import { LocalizedText, Accomplishment } from "../../services/generated/client";

interface FormField {
  id: string;
  label: string;
  type: string;
  value?: string;
  onChange?: (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => void;

  accomplishments?: Accomplishment[];
  onAccomplishmentChange?: (
    index: number,
    field: keyof Accomplishment,
    value: LocalizedText | string
  ) => void;
  onAddAccomplishment?: () => void;
  onRemoveAccomplishment?: (index: number) => void;

  options?: { label: string; value: string }[];
}

interface FormProps {
  title: string;
  fields: FormField[];
  submitLabel: string;
  onSubmit: (e: FormEvent) => void;
  isView: boolean;
  languageEditing: "en" | "ka";
}

const Form: FC<FormProps> = ({
  title,
  fields,
  submitLabel,
  onSubmit,
  isView,
  languageEditing,
}) => {
  return (
    <div className={styles.wrapper}>
      <div className={styles.container}>
        <h2 className={styles.title}>{title}</h2>
        <form onSubmit={onSubmit} className={styles.form}>
          {fields.map((field) => (
            <div className={styles.formFields} key={field.id}>
              <label className={styles.label} htmlFor={field.id}>
                {field.label}
              </label>

              {field.type === "textarea" ? (
                <textarea
                  id={field.id}
                  className={styles.textarea}
                  value={field.value}
                  onChange={field.onChange}
                />
              ) : field.type === "accomplishments" ? (
                <div className={styles.dynamicArray}>
                  {field.accomplishments?.map((accomplishment, index) => (
                    <div key={index} className={styles.group}>
                      <input
                        className={styles.input}
                        type="text"
                        placeholder="Number"
                        value={accomplishment.number}
                        onChange={(e) =>
                          field.onAccomplishmentChange?.(
                            index,
                            "number",
                            e.target.value
                          )
                        }
                      />

                      <div className={styles.languageInputs}>
                        <input
                          className={styles.input}
                          type="text"
                          placeholder={`Header (${languageEditing.toUpperCase()})`}
                          value={accomplishment.header?.[languageEditing]}
                          onChange={(e) =>
                            field.onAccomplishmentChange?.(index, "header", {
                              ...accomplishment.header,
                              [languageEditing]: e.target.value,
                            })
                          }
                        />
                      </div>

                      <div className={styles.languageInputs}>
                        <textarea
                          className={styles.textarea}
                          placeholder={`Paragraph (${languageEditing.toUpperCase()})`}
                          value={accomplishment.paragraph?.[languageEditing]}
                          onChange={(e) =>
                            field.onAccomplishmentChange?.(index, "paragraph", {
                              ...accomplishment.paragraph,
                              [languageEditing]: e.target.value,
                            })
                          }
                        />
                      </div>

                      {!isView && (
                        <button
                          type="button"
                          className={styles.removeBtn}
                          onClick={() => field.onRemoveAccomplishment?.(index)}
                        >
                          Remove
                        </button>
                      )}
                    </div>
                  ))}

                  {!isView && (
                    <button
                      type="button"
                      className={styles.addBtn}
                      onClick={field.onAddAccomplishment}
                    >
                      + Add Accomplishment
                    </button>
                  )}
                </div>
              ) : (
                <input
                  id={field.id}
                  className={styles.input}
                  type={field.type}
                  value={field.value as string}
                  onChange={field.onChange}
                />
              )}
            </div>
          ))}
          {!isView && (
            <button className={styles.primaryButton} type="submit">
              {submitLabel}
            </button>
          )}
        </form>
      </div>
    </div>
  );
};

export default Form;
